import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Field, Input, Textarea } from "./adminUi";
import { Plus, Pencil, Trash2, X, Upload, Loader2 } from "lucide-react";
import ImageUpload from "../../components/ImageUpload";
import { API } from "../../lib/api";

const EMPTY = {
  title: "", client: "", category: "Web Development", summary: "", description: "",
  cover_image: "", gallery: [], tags: [], year: String(new Date().getFullYear()), order: 0, featured: false,
};

export default function AdminPortfolio() {
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/projects").then((r) => setProjects(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm({ ...EMPTY, order: projects.length + 1 }); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...EMPTY, ...p }); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, tags: Array.isArray(form.tags) ? form.tags : [], gallery: Array.isArray(form.gallery) ? form.gallery : [] };
      if (editing === "new") await api.post("/admin/projects", payload);
      else await api.put(`/admin/projects/${editing}`, payload);
      toast.success("Saved");
      close();
      await load();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try { await api.delete(`/admin/projects/${id}`); toast.success("Deleted"); await load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-portfolio">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-fuchsia-400">Portfolio</p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-white md:text-4xl">Manage projects</h1>
        </div>
        <button onClick={openNew} data-testid="portfolio-add-btn" className="group relative inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold text-white">
          <span className="absolute inset-0 rounded-full gradient-bg" />
          <span className="relative flex items-center gap-2"><Plus size={14} /> Add project</span>
        </button>
      </div>

      <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {projects.map((p) => (
          <div key={p.id} className="overflow-hidden rounded-2xl border border-white/10 bg-[#131B2F]" data-testid={`portfolio-row-${p.slug}`}>
            {p.cover_image && <img src={p.cover_image} alt={p.title} className="h-40 w-full object-cover" />}
            <div className="p-5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.25em] text-fuchsia-300">{p.category}</span>
                {p.featured && <span className="rounded-full border border-orange-400/30 px-2 py-0.5 text-[10px] text-orange-300">Featured</span>}
              </div>
              <h3 className="mt-2 text-lg font-semibold text-white">{p.title}</h3>
              <p className="mt-1 line-clamp-2 text-sm text-white/60">{p.summary}</p>
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => openEdit(p)} data-testid={`portfolio-edit-${p.slug}`} className="rounded-lg border border-white/10 p-2 text-white/70 hover:text-white"><Pencil size={14} /></button>
                <button onClick={() => remove(p.id)} data-testid={`portfolio-delete-${p.slug}`} className="rounded-lg border border-white/10 p-2 text-white/70 hover:text-white"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="portfolio-editor-modal">
          <div className="w-full max-w-3xl overflow-hidden rounded-2xl border border-white/10 bg-[#131B2F]">
            <div className="flex items-center justify-between border-b border-white/10 p-5">
              <h3 className="text-lg font-semibold text-white">{editing === "new" ? "New project" : "Edit project"}</h3>
              <button onClick={close} className="text-white/60 hover:text-white"><X size={18} /></button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto p-6 scrollbar-thin">
              <div className="grid gap-5">
                <div className="grid gap-5 md:grid-cols-2">
                  <Field label="Title"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="project-title-input" /></Field>
                  <Field label="Client"><Input value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} data-testid="project-client-input" /></Field>
                  <Field label="Category"><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="project-category-input" /></Field>
                  <Field label="Year"><Input value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="project-year-input" /></Field>
                </div>
                <Field label="Summary"><Input value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="project-summary-input" /></Field>
                <Field label="Description"><Textarea rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="project-description-input" /></Field>
                <div>
                  <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-white/50">Cover image</span>
                  <ImageUpload
                    value={form.cover_image}
                    onChange={(url) => setForm({ ...form, cover_image: url })}
                    label=""
                    aspect="wide"
                    testId="project-cover-upload"
                  />
                </div>
                <Field label="Tags (comma-separated)">
                  <Input
                    value={(form.tags || []).join(", ")}
                    data-testid="project-tags-input"
                    onChange={(e) => setForm({ ...form, tags: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
                  />
                </Field>
                <div>
                  <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-white/50">Gallery images</span>
                  <GalleryUploader
                    items={form.gallery || []}
                    onChange={(arr) => setForm({ ...form, gallery: arr })}
                  />
                </div>
                <div className="grid gap-5 md:grid-cols-2">
                  <Field label="Order"><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "0", 10) })} /></Field>
                  <label className="mt-6 flex items-center gap-3 text-sm text-white/80">
                    <input type="checkbox" checked={!!form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="project-featured-input" />
                    Featured on home page
                  </label>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-white/10 p-5">
              <button onClick={close} className="rounded-full border border-white/15 px-5 py-2 text-xs text-white/80 hover:border-white/30">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="project-save-btn" className="group relative inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-xs font-semibold text-white disabled:opacity-60">
                <span className="absolute inset-0 rounded-full gradient-bg" />
                <span className="relative">{saving ? "Saving…" : "Save project"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function GalleryUploader({ items, onChange }) {
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10 MB)"); return; }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/uploads", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const absolute = data.url.startsWith("http") ? data.url : `${API.replace(/\/api$/, "")}${data.url}`;
      onChange([...(items || []), absolute]);
      toast.success("Image added");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Upload failed";
      toast.error(`Upload failed: ${detail}`);
    } finally {
      setUploading(false);
    }
  };

  const removeAt = (idx) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div data-testid="gallery-uploader">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
        {(items || []).map((url, i) => (
          <div key={i} className="group relative aspect-square overflow-hidden rounded-lg border border-white/10 bg-[#0B1020]">
            <img src={url} alt="" className="h-full w-full object-cover" />
            <button
              type="button"
              onClick={() => removeAt(i)}
              data-testid={`gallery-remove-${i}`}
              className="absolute right-1.5 top-1.5 rounded-full bg-black/70 p-1 text-white/80 opacity-0 transition-opacity group-hover:opacity-100"
              aria-label="Remove"
            >
              <X size={12} />
            </button>
          </div>
        ))}
        <label className="flex aspect-square cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-white/15 bg-[#0B1020] text-xs text-white/50 hover:border-white/30 hover:text-white">
          {uploading ? <Loader2 size={20} className="animate-spin" /> : <Upload size={20} />}
          <span>{uploading ? "Uploading…" : "Add image"}</span>
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
            className="hidden"
            data-testid="gallery-add-input"
            onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
          />
        </label>
      </div>
      <p className="mt-2 text-[11px] text-white/40">
        PNG, JPG, WebP, GIF, SVG · up to 10 MB each
      </p>
    </div>
  );
}
